import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';

import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import type { UserBase } from '@recruitee/account-types';
import { InterviewAnswerListComponent } from '@recruitee/components/interview-answer-list';
import { I18nService, TranslatePipe } from '@recruitee/i18n';
import { InterviewResultsActions } from '@recruitee/interview-results-data';
import type {
  InterviewResult,
  InterviewResultAnswer,
  RatingLevelTranslations,
} from '@recruitee/interview-results-types';
import { AppNavigatorService } from '@recruitee/navigation';
import type { OfferFull } from '@recruitee/offers-types';
import { OfferKind } from '@recruitee/offers-types';
import { PlacementInfo } from '@recruitee/offers-ui';
import type { Stage } from '@recruitee/stages-types';
import {
  DialogService,
  RtStatefulComponent,
  AvatarComponent,
  ButtonLoadingDirective,
  IconButtonComponent,
  GroupComponent,
  HeadingComponent,
  DividerComponent,
  BoxComponent,
  InterviewEvaluationRatingComponent,
  ButtonComponent,
  OfferStatusIndicatorComponent,
} from '@recruitee/user-interface';
import { generateUuid } from '@recruitee/utils';

import { InterviewKind } from '../../interview-kind';
import { QuickEvaluationFormComponent } from '../../quick-evaluation-form/quick-evaluation-form.component';
import { InterviewMemberComponent } from '../interview-member/interview-member.component';

type State = {
  showMore: boolean;
  showMoreEnabled: boolean;
  quickEditEnabled: boolean;
  deleteLoading: boolean;
  answers: InterviewResultAnswer[];
};

@Component({
  selector: 'rt-interview-result-card',
  templateUrl: 'interview-result-card.component.html',
  styleUrls: ['./interview-result-card.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    BoxComponent,
    NgTemplateOutlet,
    DividerComponent,
    HeadingComponent,
    InterviewAnswerListComponent,
    ButtonComponent,
    QuickEvaluationFormComponent,
    InterviewEvaluationRatingComponent,
    InterviewMemberComponent,
    GroupComponent,
    IconButtonComponent,
    ButtonLoadingDirective,
    AvatarComponent,
    OfferStatusIndicatorComponent,
    TranslatePipe,
  ],
})
export class InterviewResultCardComponent extends RtStatefulComponent<State> {
  @ViewChild('cardBody') public cardBody: ElementRef;

  @Input() public result: InterviewResult & {
    offer?: Pick<OfferFull, 'kind' | 'status' | 'title'>;
    stage?: Pick<Stage, 'name'>;
  };
  @Input() public candidate: { id: number };
  @Input() public edit: boolean = true;
  @Input() public currentAdmin?: UserBase;
  @Input() public hasHeader: boolean = false;
  @Input() public isFairEvalEnabledForStage: boolean = false;
  @Input() public ratingLevelTranslations?: RatingLevelTranslations;
  @Input() public abilities? = {
    highLevelDataAccess: false,
    viewCandidates: false,
    manageCandidates: false,
  };

  public assignedPlacement?: PlacementInfo | null;

  public sanitizedNote: SafeHtml;

  private ngOnDestroy$ = new Subject<void>();
  private readonly MAX_VISIBLE_HEIGHT: number = 240;

  constructor(
    public sanitizer: DomSanitizer,
    protected cdRef: ChangeDetectorRef,
    private dialogService: DialogService,
    private viewRef: ViewContainerRef,
    private router: Router,
    private i18n: I18nService,
    private appNavigator: AppNavigatorService,
    private actions$: Actions,
    private store: Store,
  ) {
    super(cdRef);
    this.createState({
      showMore: false,
      showMoreEnabled: false,
      quickEditEnabled: false,
      deleteLoading: false,
      answers: [],
    });
  }

  public ngOnInit(): void {
    this.setAssignedPlacement();
    this.subscribeToEvents();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const answers =
      changes.result &&
      changes.result.currentValue &&
      changes.result.currentValue.interviewResultAnswers;

    if (answers) {
      this.setState({
        ...this.state,
        answers: answers.filter((answer: InterviewResultAnswer) => answer.questionKind !== 'info'),
      });
    }

    if ('result' in changes) {
      // eslint-disable-next-line ban/ban
      this.sanitizedNote = this.sanitizer.bypassSecurityTrustHtml(this.result.ratingNote!);
      this.cdRef.detectChanges();
    }
  }

  public ngAfterViewInit(): void {
    this.checkShowMore();
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public openCandidate(result: InterviewResult): void {
    this.appNavigator.openCandidateDialog(result.candidateId);
  }

  public openOffer(result: InterviewResult): void {
    const { offer, talentPool } = result;
    if (!offer && !talentPool) return;
    const { kind, slug } = (offer || talentPool)!;

    const url =
      kind === OfferKind.Job ? `/offers/${slug}/pipeline` : `/talent_pools/${slug}/filters`;

    this.router.navigateByUrl(url);
  }

  public checkShowMore(): void {
    if (this.cardBody) {
      const height = this.cardBody.nativeElement.scrollHeight;

      if (height > this.MAX_VISIBLE_HEIGHT) {
        this.setState({ ...this.state, showMoreEnabled: true });
      }
    }
  }

  public toggleShowMore(): void {
    this.setState({ ...this.state, showMore: !this.state.showMore });
  }

  public editInterviewResult(): void {
    if (this.result.kind === InterviewKind.QUICK) {
      this.openQuickEvaluationForm();
    } else {
      this.appNavigator.openCandidateDialog(this.candidate.id, {
        tab: 'interviews',
        openInterview: true,
        interviewEdit: true,
        interviewResultId: this.result.id,
      });
    }
  }

  public close(): void {
    this.setState({ ...this.state, quickEditEnabled: false });
  }

  public canEdit(): boolean {
    return !!(
      this.currentAdmin &&
      this.result.adminId === this.currentAdmin!.id &&
      this.abilities?.viewCandidates
    );
  }

  public delete(): void {
    const message = this.i18n.translate('app.interview.delete_message');
    const title = this.i18n.translate('app.interview.delete_title');

    this.openConfirmDialog(message, title, 'error').subscribe(confirmed => {
      if (confirmed) {
        this.deleteInterviewResult();
      }
    });
  }

  private openConfirmDialog(message: string, title: string, variant: string): Observable<boolean> {
    return this.dialogService.confirm(
      { title, message, variant, isCancelable: true, safeFooter: true },
      this.viewRef,
    );
  }

  private deleteInterviewResult(): void {
    this.setState({ ...this.state, deleteLoading: true });
    this.store.dispatch(
      InterviewResultsActions.deleteOnePessimistic({
        payload: {
          data: {
            id: this.result && this.result.id,
          },
          correlationId: generateUuid(),
        },
      }),
    );
  }

  private openQuickEvaluationForm(): void {
    this.setState({ ...this.state, quickEditEnabled: true });
  }

  private setAssignedPlacement(): void {
    const offer = this.result.offer || this.result.talentPool;
    this.assignedPlacement = offer && {
      status: offer.status,
      kind: offer.kind,
      title: offer.title,
      stageName: this.result?.stage?.name,
    };
  }

  private subscribeToEvents(): void {
    this.actions$
      .pipe(
        ofType(
          InterviewResultsActions.deleteOnePessimisticSuccess,
          InterviewResultsActions.deleteOnePessimisticFailed,
        ),
      )
      .pipe(takeUntil(this.ngOnDestroy$.asObservable()))
      .subscribe(() => {
        this.setState({ ...this.state, deleteLoading: false });
      });
  }
}
